import moment from 'moment';
import { Reducer } from 'redux';
import { action, ActionType } from 'typesafe-actions';

import { ThunkResult } from '../../typing/types';
import { OrganizationSchedule } from '../../utils/classes';
import { currentPeriodStartDate, currentPeriodEndDate } from '../../utils/schedule';
import { logErrorWithContext } from '../../utils/sentry/sentrySetup';
import { NIMBLY_API_SCHEDULES_LITE_ACTIVE_PERIOD } from '../../constants/api';
import type { ScheduleApiFilterParams } from '../../typing/scheduleFilter';
import API from '../helpers/api';
import { mapActiveScheduleToScheduleItems, type ActiveScheduleResponse } from '../../utils/scheduleMapper';
import type { ScheduleListItemData } from '../../typing/scheduleItem';

export const SET_ACTIVE_SCHEDULE_ITEMS = '@activeSchedule/SET_ACTIVE_SCHEDULE_ITEMS';
export const SET_IS_FETCHING_ACTIVE = '@activeSchedule/SET_IS_FETCHING_ACTIVE';
export const SET_ACTIVE_SCHEDULE_ERROR = '@activeSchedule/SET_ACTIVE_SCHEDULE_ERROR';

const setActiveScheduleItems = (items: ScheduleListItemData[], totalPercentage: number, now: number = Date.now()) =>
  action(SET_ACTIVE_SCHEDULE_ITEMS, { items, totalPercentage, now });

const setIsFetchingActive = (bool: boolean) => action(SET_IS_FETCHING_ACTIVE, { bool });

const setActiveScheduleError = (hasError: boolean) => action(SET_ACTIVE_SCHEDULE_ERROR, { hasError });

/**
 * Unwrap the active-period response body. The /v2 endpoint returns
 * `res.data = { details, totalPercentage }`; the usecase-style wrapper may
 * nest one level deeper (`res.data.data = { details, ... }`). Handle both.
 */
const extractActiveSchedule = (body: any): ActiveScheduleResponse | null => {
  if (body?.details) {
    return body as ActiveScheduleResponse;
  }
  if (body?.data?.details) {
    return body.data as ActiveScheduleResponse;
  }
  return null;
};

/**
 * Fetch every schedule in the organization's current active period and store
 * them as a flat, date-tagged list. The home screen groups them by date.
 * @param organizationSchedule org schedule (defines the active period range)
 * @param filterParams optional site/status/type filters (mirrors getMyScheduledSites)
 */
const getActivePeriodSchedules =
  (organizationSchedule: OrganizationSchedule, filterParams?: ScheduleApiFilterParams): ThunkResult =>
  async (dispatch, getState) => {
    const state = getState();
    const { isConnected } = state.network;

    if (!isConnected) {
      return;
    }

    const now = moment().valueOf();
    const startMoment = currentPeriodStartDate(organizationSchedule) || moment();
    const endMoment = currentPeriodEndDate(organizationSchedule, startMoment.clone()) || moment();
    const startDate = startMoment.format('YYYY-MM-DD');
    const endDate = endMoment.format('YYYY-MM-DD');

    dispatch(setIsFetchingActive(true));

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = new URLSearchParams({ startDate, endDate, timezone, forOfflineSync: 'true' });
      if (filterParams?.sites?.length) {
        filterParams.sites.forEach(s => params.append('sites', s));
      }
      if (filterParams?.status?.length) {
        // API expects lowercase-kebab: not-started, in-progress, completed, overdue
        filterParams.status.forEach(s => params.append('status', s.toLowerCase().replace(/_/g, '-')));
      }
      if (filterParams?.types?.length) {
        // API expects lowercase: scheduled, adhoc
        filterParams.types.forEach(t => params.append('types', t.toLowerCase()));
      }
      if (filterParams?.scheduleType?.length) {
        filterParams.scheduleType.forEach(s => params.append('scheduleType', s));
      }

      const api = await API.withToken();
      const res = await api.getJSON(`${NIMBLY_API_SCHEDULES_LITE_ACTIVE_PERIOD}?${params.toString()}`);
      const activeSchedule = extractActiveSchedule(res?.data);

      if (activeSchedule) {
        const items = mapActiveScheduleToScheduleItems(activeSchedule);
        dispatch(setActiveScheduleItems(items, activeSchedule.totalPercentage ?? 0, now));
      } else {
        logErrorWithContext(
          new Error('Active period schedule API returned null or missing data'),
          'schedule_lite_active_period_empty_response',
          { startDate, endDate, responseIsNull: res === null }
        );
        dispatch(setActiveScheduleError(true));
      }
    } catch (err) {
      logErrorWithContext(
        err instanceof Error ? err : new Error(String(err)),
        'schedule_lite_active_period_fetch_error',
        { startDate, endDate }
      );
      dispatch(setActiveScheduleError(true));
    } finally {
      dispatch(setIsFetchingActive(false));
    }
  };

export const activeScheduleActions = {
  setActiveScheduleItems,
  setIsFetchingActive,
  setActiveScheduleError
};

export const activeScheduleThunk = {
  getActivePeriodSchedules
};

export type ActiveScheduleAction = ActionType<typeof activeScheduleActions>;

export type ActiveScheduleState = {
  readonly items: ScheduleListItemData[];
  readonly totalPercentage: number;
  readonly isFetching: boolean;
  readonly hasError: boolean;
  readonly timestamp: number | null;
};

const initialState: ActiveScheduleState = {
  items: [],
  totalPercentage: 0,
  isFetching: false,
  hasError: false,
  timestamp: null
};

export const activeScheduleReducer: Reducer<ActiveScheduleState, ActiveScheduleAction> = (
  state = initialState,
  incomingAction
) => {
  switch (incomingAction.type) {
    case SET_ACTIVE_SCHEDULE_ITEMS:
      return {
        ...state,
        items: incomingAction.payload.items,
        totalPercentage: incomingAction.payload.totalPercentage,
        timestamp: incomingAction.payload.now,
        hasError: false
      };
    case SET_IS_FETCHING_ACTIVE:
      return {
        ...state,
        isFetching: incomingAction.payload.bool,
        // clear error when a new fetch begins
        ...(incomingAction.payload.bool ? { hasError: false } : {})
      };
    case SET_ACTIVE_SCHEDULE_ERROR:
      return {
        ...state,
        hasError: incomingAction.payload.hasError,
        isFetching: false
      };
    default:
      return state;
  }
};
